import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';

import Text from 'shopper/components/Text';

import { isObj } from 'lib/object';
import { markup } from 'lib/utils';

const StyledTextWithRichContent = styled(Text)`
  ${({ theme }) => css`
    hyphens: auto;
    word-break: break-word;

    a {
      line-break: anywhere;

      &:hover {
        color: ${theme.colors.link.primary.foregroundHover};
        text-decoration: underline;
      }
    }

    img {
      max-height: 15rem;
      max-width: 13rem;
    }

    iframe,
    video {
      max-height: 12rem;
      max-width: 100%;
    }

    ${theme.mediaQueries.md} {
      img {
        max-height: 25rem;
        max-width: 25rem;
      }
    }

    ${theme.mediaQueries.lg} {
      img {
        max-height: 37.5rem;
        max-width: 37.5rem;
      }

      iframe,
      video {
        max-height: 16rem;
      }
    }
  `}
  ${({ full }) =>
    full &&
    css`
      width: 100%;
    `}
`;

const TextWithRichContent = ({ children, ...props }) => {
  if (isObj(children) || Array.isArray(children)) {
    return (
      <StyledTextWithRichContent as="span" {...props}>
        {children}
      </StyledTextWithRichContent>
    );
  }

  return (
    <StyledTextWithRichContent
      as="span"
      dangerouslySetInnerHTML={markup(children)}
      {...props}
    />
  );
};

Text.propTypes = {
  /**
   * Renders a bold text
   */
  bold: PropTypes.bool,
  /**
   * Text or children to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Renders a italic text
   */
  italic: PropTypes.bool,
  /**
   * `line-break` css prop
   */
  lineBreak: PropTypes.string,
  /**
   * Renders a text in uppercase
   */
  upper: PropTypes.bool,
  /**
   * Renders a text with a determined size
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3']),
  /**
   * `white-space` css prop
   */
  whiteSpace: PropTypes.string,
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [typography](https://styled-system.com/api#typography) utility
   */
  ...propTypes.typography,
};

export default TextWithRichContent;
